import type { TrackDirection } from './topology';
import type { LayoutElement } from '../types';
import {
  getCurveSweepAngle,
  getCurveSweepSign,
  isCurveRailType,
  isJunctionRailType,
  isTrackUnidirectional,
  resolveRailType,
} from './topology';

export function trackDirectionSymbol(direction: TrackDirection): string {
  return direction === 'bi' ? '<>' : '>';
}

/** Midpoint + tangent (deg) for a straight segment in local coordinates (0,0)→(w,0). */
export function straightTrackDirectionAnchor(
  width: number,
  height: number
): { x: number; y: number; rotation: number } {
  return { x: width / 2, y: height / 2, rotation: 0 };
}

/** Midpoint + tangent (deg) along a curve arc in local coordinates. */
export function curveTrackDirectionAnchor(
  radius: number,
  sweepAngle: number,
  sweepSign: 1 | -1
): { x: number; y: number; rotation: number } {
  const signedSweep = sweepSign * sweepAngle;
  const midAngle = signedSweep / 2;
  const rad = (midAngle * Math.PI) / 180;
  return {
    x: radius * Math.cos(rad),
    y: radius * Math.sin(rad),
    rotation: midAngle + 90 * sweepSign,
  };
}

/** Midpoint + tangent (deg) for a line from (x1,y1) to (x2,y2). */
export function lineTrackDirectionAnchor(
  x1: number,
  y1: number,
  x2: number,
  y2: number
): { x: number; y: number; rotation: number } {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return {
    x: (x1 + x2) / 2,
    y: (y1 + y2) / 2,
    rotation: (Math.atan2(dy, dx) * 180) / Math.PI,
  };
}

/** Hide markers on segments too small to read at the current zoom. */
export function shouldShowTrackDirectionMarker(
  segmentLength: number,
  zoomLevel: number
): boolean {
  const safeZoom = Math.max(zoomLevel, 0.05);
  return segmentLength * safeZoom >= 18;
}

const normDeg = (deg: number) => ((deg % 360) + 360) % 360;

/**
 * True when the properties panel should offer "Switch direction" — uni rails
 * only (not bi, not junctions).
 */
export function canReverseTrackDirection(el: LayoutElement): boolean {
  if (!isTrackUnidirectional(el)) return false;
  const railType = resolveRailType(el);
  return !isJunctionRailType(railType);
}

/**
 * Absolute shortest angle between two headings in [0, 180].
 */
export function shortestHeadingDeltaDeg(a: number, b: number): number {
  return Math.abs(((a - b) % 360 + 540) % 360 - 180);
}

/**
 * Align a uni rail’s stored travel direction with an intended polyline heading.
 *
 * Axis-aligned straights are often canonicalized to 0°/90° for footprint identity;
 * when that opposes draw order (~180° apart), reverse in place so arrows and
 * simulation follow the path the user drew. Curves are left unchanged — Point
 * Draw fillets already encode travel in position/rotation.
 */
export function alignTrackToTravelHeading(
  el: LayoutElement,
  travelHeadingDeg: number,
  tolDeg = 1
): LayoutElement {
  if (!canReverseTrackDirection(el)) return el;
  if (isCurveRailType(resolveRailType(el))) return el;

  const storedHeading = el.rotation || 0;
  if (Math.abs(shortestHeadingDeltaDeg(storedHeading, travelHeadingDeg) - 180) > tolDeg) {
    return el;
  }

  const updates = reverseUnidirectionalTrack(el);
  if (!updates) return el;
  return {
    ...el,
    position: updates.position,
    rotation: updates.rotation,
    customProperties: updates.customProperties,
  };
}

/**
 * Flips travel direction of a unidirectional rail without moving its footprint.
 *
 * Straight: rotate 180° about the segment centre so endpoints stay put but
 * swap roles (arrow + simulation edge reverse).
 *
 * Curve: advance rotation by the signed sweep and negate `curveSweepSign` so
 * the arc occupies the same space with endpoints swapped.
 */
export function reverseUnidirectionalTrack(
  el: LayoutElement
): Pick<LayoutElement, 'position' | 'rotation' | 'customProperties'> | null {
  if (!canReverseTrackDirection(el)) return null;

  const railType = resolveRailType(el);
  const props = { ...(el.customProperties || {}) };

  if (isCurveRailType(railType)) {
    const sign = getCurveSweepSign(el);
    const sweep = getCurveSweepAngle(railType);
    return {
      position: { ...el.position },
      rotation: normDeg((el.rotation || 0) + sign * sweep),
      customProperties: {
        ...props,
        curveSweepSign: (sign === 1 ? -1 : 1) as 1 | -1,
      },
    };
  }

  // Straight (and any non-curve, non-junction rail drawn as a centreline).
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const cx = el.position.x + (w / 2) * cos - (h / 2) * sin;
  const cy = el.position.y + (w / 2) * sin + (h / 2) * cos;

  const newRotation = normDeg((el.rotation || 0) + 180);
  const nr = (newRotation * Math.PI) / 180;
  const ncos = Math.cos(nr);
  const nsin = Math.sin(nr);

  return {
    position: {
      x: cx - (w / 2) * ncos + (h / 2) * nsin,
      y: cy - (w / 2) * nsin - (h / 2) * ncos,
      z: el.position.z,
    },
    rotation: newRotation,
    customProperties: props,
  };
}
